import * as d3 from 'd3'
import * as dc from 'dc'
import { HOUR_LABELS } from '@/utils/flowSightHourBuckets'
import { BUY_COLOR, SELL_COLOR } from '@/utils/flowSightColors'
import { isConverted } from '../calculations/calculateConvertedVolumeBRL.js'
import { formatCompactAmount } from '@/utils/utils'

const TENOR_COUNT = HOUR_LABELS.length
const LABEL_W = 80


export function createVolumeBySideChart ({ el, chartGroup, dims, height, uid, drilldown, store }) {
  const host = el
  if (!host) return null

  // CONVERTED volume: BRL notional of DEAL rows only. The tab as a whole sees
  // DEAL + NOTH.DONE (see Conversions.vue::_getStoreRows), which is the right
  // population for a conversion RATE - but the wrong one for this chart, which
  // answers "how much did we actually close", not "how much did we quote".
  //
  // Same basis as the bubble's radius (grpNotionalIdxSideBubble) so the two
  // halves of the tab stay comparable, and in BRL via store.convertNotionalToBRL
  // like the currency/KPI charts.
  const amountOf = (d) => (isConverted(d) ? store.convertNotionalToBRL(d) || 0 : 0)

  const labelOf = (i) => HOUR_LABELS[i] || ''

  const visibleTenorIdxs = () => {
    return d3.range(TENOR_COUNT - 1, -1, -1) // always [4,3,...,0] — selection uses CSS dimming, not row removal
  }

  // These are reduceSum groups, so a band whose rows were all filtered out does
  // not come back as exactly 0: crossfilter maintains the sum incrementally and
  // add-then-subtract of the same BRL amounts leaves a residue around 1e-12.
  // Invisible as a bar, but it poisons the axis - d3.tickStep over a 7e-12 max
  // labels the ticks "2p", "4p". No `cnt` here to gate on, hence the epsilon.
  const RESIDUE = 1e-6
  const clean = (v) => (Math.abs(v) < RESIDUE ? 0 : v)

  const ensureVisibleTenors = (grp) => ({
    all: () => {
      const map = new Map(grp.all().map((d) => [d.key, d.value]))
      return visibleTenorIdxs().map((i) => ({ key: i, value: clean(map.get(i) || 0) }))
    },
  })
  const grpSellAxis = dims.dimTenorIdxOnlyGroups.group().reduceSum((d) => (d.side === 'SELL' ? amountOf(d) : 0))
  const grpBuyAxis = dims.dimTenorIdxOnlyGroups.group().reduceSum((d) => (d.side === 'BUY' ? amountOf(d) : 0))

  const grpSell = ensureVisibleTenors(grpSellAxis)
  const grpBuy = ensureVisibleTenors(grpBuyAxis)

  let labelsHost, sellHost, buyHost, rowSell, rowBuy, rendered

  const build = () => {
    while (host.firstChild) host.removeChild(host.firstChild)

    // gap/padding/margin all explicitly zeroed so the border-right on
    // sellHost is the ONLY visual separator between SELL and BUY.
    const wrap = document.createElement('div')
    wrap.style.cssText = 'display:flex; gap:0; padding:0; margin:0; width:100%; height:100%; align-items:stretch;'
    host.appendChild(wrap)

    labelsHost = document.createElement('div')
    labelsHost.style.cssText = `position:relative; flex:0 0 ${LABEL_W}px; padding:0; margin:0;`
    wrap.appendChild(labelsHost)

    sellHost = document.createElement('div')
    sellHost.style.cssText = 'flex:1 1 0; min-width:0; padding:0; margin:0; border-right:1px solid rgba(189,189,189,0.2);'
    wrap.appendChild(sellHost)

    buyHost = document.createElement('div')
    buyHost.style.cssText = 'flex:1 1 0; min-width:0; padding:0; margin:0;'
    wrap.appendChild(buyHost)

    rowSell = dc.rowChart(sellHost)
    rowBuy = dc.rowChart(buyHost)
    // Rendered/redrawn only via this composite's update() below (which
    // recomputes the shared value domain first) - never directly by a
    // dc.redrawAll(group) cascade, which would redraw with a stale x scale.
    dc.deregisterChart(rowSell)
    dc.deregisterChart(rowBuy)

    const handleRowClick = (datum) => {
      const idx = datum.key
      drilldown.toggle('splitVolume', idx, { clear: ['bubble'] })
    }

    // dc's own hasFilter()/filters() only know about a chart's private
    // _filters array - override so both rows' "selected"/"deselected" CSS
    // classes read the one shared Set instead, keeping BUY/SELL rows for
    // the same tenor in sync with each other.
    const hasFilterHandler = (_filters, filter) =>
      (filter === undefined || filter === null)
        ? drilldown.hasSelections()
        : drilldown.selectedTenorIdxs().includes(filter)

    ;[[rowSell, grpSell], [rowBuy, grpBuy]].forEach(([chart, grp]) => {
      chart
        .dimension(dims.dimTenorIdxOnlyGroups)
        .group(grp)
        .keyAccessor((d) => d.key)
        .valueAccessor((d) => d.value)
        .ordering((kv) => -kv.key) // chronological, tenor 0 (1-30D) at the bottom
        .gap(5)
        .renderLabel(false)
        .renderTitle(false)
        .elasticX(false)
        .hasFilterHandler(hasFilterHandler)
      chart.onClick = handleRowClick
    })
    rowSell.colors(() => SELL_COLOR)
    rowBuy.colors(() => BUY_COLOR)

    rendered = false
  }

  const renderLabels = (margins, gap, heightPx, visibleIdxs) => {
    labelsHost.querySelectorAll('.tenor-label').forEach((el) => el.remove())
    const n = Math.max(1, visibleIdxs.length)
    const effH = heightPx - margins.top - margins.bottom
    const rowH = Math.max(4, (effH - (n + 1) * gap) / n)
    visibleIdxs.forEach((tenorIdx, pos) => {
      const top = margins.top + (pos + 1) * gap + pos * rowH
      const labelEl = document.createElement('div')
      labelEl.className = 'tenor-label'
      labelEl.textContent = labelOf(tenorIdx)
      labelEl.style.cssText = `position:absolute; left:0; right:6px; top:${top}px; height:${rowH}px; display:flex; align-items:center; justify-content:flex-end; font-size:11px; color:var(--muted); white-space:nowrap;`
      labelsHost.appendChild(labelEl)
    })
  }

  const update = () => {
    if (!rowSell) build()

    const visibleIdxs = visibleTenorIdxs()
    const totalW = Math.max(300, host.clientWidth || 600)
    const H = height + 30

    // Outer margins hold the FIRST/LAST axis tick label, which is centred on the
    // tick and so needs about half its own width outside the plot - "400M" at
    // 10px is ~28px, hence 20. At 10 the outermost label came out clipped
    // ("00M"). The inner margins stay at 0: that edge is the shared centre and
    // its "0" tick is drawn by the other half.
    const marginsSell = { top: 16, right: 0, bottom: 40, left: 20 }
    const marginsBuy = { top: 16, right: 20, bottom: 40, left: 0 }

    const measuredSellW = sellHost.clientWidth
    const measuredBuyW = buyHost.clientWidth
    const eachW = (measuredSellW > 0 && measuredBuyW > 0)
      ? Math.min(measuredSellW, measuredBuyW)
      : Math.max(120, Math.floor((totalW - LABEL_W) / 2))

    const maxAbsData = Math.max(
      d3.max(grpSellAxis.all(), (r) => Math.abs(clean(r.value))) || 0,
      d3.max(grpBuyAxis.all(), (r) => Math.abs(clean(r.value))) || 0
    ) || 1
    const desired = 5
    const step = d3.tickStep(0, maxAbsData, desired) || 1
    const maxTick = Math.ceil(maxAbsData / step) * step
    const ticks = d3.range(0, maxTick + step * 0.5, step)
    const fmt = d3.format('~s')
    const tickFormat = (d) => fmt(Math.abs(d)).replace('G', 'M')

    const innerW = Math.max(60, eachW - (marginsSell.left + marginsSell.right))
    const xSell = d3.scaleLinear().domain([0, maxTick]).range([innerW, 0])
    const xBuy = d3.scaleLinear().domain([0, maxTick]).range([0, innerW])

    rowSell.width(eachW).height(H).margins(marginsSell).x(xSell)
    rowSell.xAxis().tickValues(ticks).tickFormat(tickFormat)

    rowBuy.width(eachW).height(H).margins(marginsBuy).x(xBuy)
    rowBuy.xAxis().tickValues(ticks).tickFormat(tickFormat)

    renderLabels(marginsSell, rowSell.gap(), H, visibleIdxs)

    if (!rendered) {
      rowSell.render()
      rowBuy.render()
      rendered = true
    } else {
      rowSell.redraw()
      rowBuy.redraw()
    }
    drilldown.applyTransition(d3.select(sellHost).selectAll('g.row rect'))
    drilldown.applyTransition(d3.select(buyHost).selectAll('g.row rect'))

    drawValueLabels(sellHost, 'SELL', xSell)
    drawValueLabels(buyHost, 'BUY', xBuy)
  }

  // Hand-drawn instead of dc's .renderLabel(): a RowChart label inherits the
  // same translateX as its rect, which on the MIRRORED sell scale lands at the
  // outer tip while the buy one lands at the origin - the two wings would be
  // labelled in different places. Positioning them here keeps the butterfly
  // symmetric: both sit at the OUTER end of their own bar.
  //
  // Inside the bar when there is room, just outside it when the bar is too
  // short for the text to fit.
  const MIN_INSIDE_PX = 58
  const drawValueLabels = (host, side, scale) => {
    const root = d3.select(host)
    root.selectAll('text.row-value').remove()

    // Geometry comes from the SCALE, never from the rendered rect. dc sets a
    // row's `height` immediately but animates `width` and `transform`, so on
    // the first render those attributes still read 0 - which silently dropped
    // every label. Only `height` is safe to read back.
    const zero = scale(0)

    root.selectAll('g.row').each(function () {
      const row = d3.select(this)
      const rect = row.select('rect')
      if (rect.empty()) return

      const datum = row.datum()
      const value = datum && typeof datum.value === 'number' ? datum.value : 0
      if (!value) return

      const tip = scale(value)
      const width = Math.abs(zero - tip)
      if (width <= 0) return
      const x0 = Math.min(zero, tip)
      const height = (+rect.attr('height')) || 0

      const inside = width >= MIN_INSIDE_PX
      // BUY grows to the right, SELL to the left: the outer tip is the far end
      // in each direction.
      const outerX = side === 'BUY' ? x0 + width : x0
      const pad = inside ? -6 : 6
      const x = side === 'BUY' ? outerX + pad : outerX - pad

      // The anchor flips with which side of the tip the text sits on, not with
      // the side of the chart. Anchoring an outside label like an inside one
      // makes it grow back ACROSS the bar and off the edge of the svg - which
      // is how "R$ 22.77Mi" rendered as a lone "R$".
      const anchor = side === 'BUY'
        ? (inside ? 'end' : 'start')
        : (inside ? 'start' : 'end')

      row.append('text')
        .attr('class', 'row-value')
        .attr('x', x)
        .attr('y', height / 2 + 4)
        .attr('text-anchor', anchor)
        .text(`R$ ${formatCompactAmount(value)}`)
        // !important because Conversions.vue paints every .dc-chart text with
        // var(--muted) !important, which an ordinary inline fill loses to.
        .attr('style', 'font-size:10px; pointer-events:none; fill:#fff !important')
    })
  }

  const chartObj = {
    render: () => update(),
    redraw: () => update(),
    filterAll: () => {
      drilldown.clear('splitVolume', { redraw: false })
      if (rendered) update()
      return chartObj
    },
    filters: () => drilldown.values('splitVolume'),
    anchorName: () => 'flowsightChart3_' + uid,
  }

  dc.registerChart(chartObj, chartGroup)
  update()
  return chartObj
}
